// 用網址的家戶 + 事件 ID 找回一筆事件（P0-2：事件詳細可以直接用網址開——新分頁、通知、推播、email、書籤）。
// 先打單筆事件端點 GET /v1/homes/{home}/vms/events/{event}（SPEC #vms-web-909；id 收 evt-<ULID>、mev-<hex>、裸 ObjectID hex）：
//   200 → 那一筆（形狀同 façade 列表一筆 + level / ai_tags / has_clip / home_id）；404 NOT_FOUND → 真的找不到（不存在、過保存期、沒權限一律這個）。
// 端點不能用（舊後端沒有這條路由、5xx、網路）才退回舊的兩條路當備援：
//   1. 全文檢索 GET /v1/vms/search/events（event_id = Mongo ObjectID 的 hex）
//   2. VMS façade GET /v1/homes/{id}/vms/events（event_id = feed 的 evt-<ULID>，或 mev-<hex>）
// 兩種 ID 都帶時間（ObjectID 前 4 bytes = 寫入秒、ULID 前 48 bit = 毫秒），用它圈一段時間來找：
// 寫入時間 ≥ 發生時間（設備離線補傳會晚很多），所以往前找 24 小時、往後 10 分鐘，最多翻 5 頁。
import { isKinioError, kinioFetch, planeBase, vms } from '@kinio/web-sdk';
import { searchApi, type SearchDoc } from './planeApi';
import type { VmsFacadeEvent } from '@kinio/web-sdk';

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const BACK_S = 24 * 3600;
const AHEAD_S = 600;
const MAX_PAGES = 5;

/** 事件 ID 裡的時間（unix 秒）；認不出來 = null。 */
export function eventIdTime(id: string): number | null {
  const hex = id.match(/^(?:mev-)?([0-9a-f]{24})$/i);
  if (hex) return parseInt(hex[1].slice(0, 8), 16);
  const ulid = id.match(/(?:^|-)([0-9A-HJKMNP-TV-Z]{26})$/i);
  if (ulid) {
    let ms = 0;
    for (const c of ulid[1].slice(0, 10).toUpperCase()) {
      const v = CROCKFORD.indexOf(c);
      if (v < 0) return null;
      ms = ms * 32 + v;
    }
    return Math.floor(ms / 1000);
  }
  return null;
}

/** 同一筆事件在兩個來源的 ID 寫法（hex ↔ mev-hex）。 */
export function idVariants(id: string): Set<string> {
  const out = new Set([id]);
  if (/^[0-9a-f]{24}$/i.test(id)) out.add('mev-' + id);
  if (id.startsWith('mev-')) out.add(id.slice(4));
  return out;
}

export type FoundEvent = { doc: SearchDoc } | { ev: VmsFacadeEvent };

/** 單筆事件端點；404 NOT_FOUND = 真的找不到（回 null）；其他錯誤往上丟（呼叫端退回舊的找法）。 */
export async function fetchEvent(api: string, homeId: string, eventId: string): Promise<VmsFacadeEvent | null> {
  try {
    return await kinioFetch<VmsFacadeEvent>(`${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/vms/events/${encodeURIComponent(eventId)}`);
  } catch (e) {
    if (isKinioError(e) && e.status === 404 && e.code === 'NOT_FOUND') return null;
    throw e;
  }
}

export async function lookupEvent(api: string, homeId: string, eventId: string, now = Date.now() / 1000): Promise<FoundEvent | null> {
  try {
    const one = await fetchEvent(api, homeId, eventId);
    return one ? { ev: one } : null;
  } catch {
    /* 端點不能用：退回舊的找法 */
  }
  return legacyLookup(api, homeId, eventId, now);
}

async function legacyLookup(api: string, homeId: string, eventId: string, now: number): Promise<FoundEvent | null> {
  const ids = idVariants(eventId);
  const t = eventIdTime(eventId);
  const from = (t ?? now) - BACK_S;
  const to = Math.min((t ?? now) + AHEAD_S, now + AHEAD_S);
  const iso = (s: number) => new Date(s * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z');
  // 1. 全文檢索（ObjectID 那種 ID 在這裡）
  try {
    let cursor: string | undefined;
    for (let i = 0; i < MAX_PAGES; i++) {
      const r = await searchApi.events(api, { q: `after:${iso(from)} before:${iso(to)}`, homes: [homeId], cursor, limit: 200 });
      const hit = (r?.data ?? []).find((d) => ids.has(d.event_id));
      if (hit) return { doc: hit };
      cursor = r?.next_cursor;
      if (!cursor) break;
    }
  } catch {
    /* 檢索不能用：再試 façade */
  }
  // 2. façade（feed 的 evt- ID、mev- ID）
  let cursor: string | undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const r = await vms.events(api, homeId, { from: iso(from), to: iso(to), limit: 200, cursor });
    const hit = (r?.data ?? []).find((e) => ids.has(e.event_id));
    if (hit) return { ev: hit };
    cursor = r?.next_cursor ?? undefined;
    if (!cursor) break;
  }
  return null;
}
